import React from "react"
import PropTypes from "prop-types"
import {
  Col,
  Row,
  Card,
  CardBody,
  Label,
  Input,
} from "reactstrap"

/**
 * EstimateCostBlock - Shared component for displaying/editing Estimate Cost
 * @param {string} mode - "view" for display only, "edit" for editable inputs
 * @param {object} data - The estimate cost data
 * @param {function} onUnitChange - Callback when unit changes (edit mode only)
 */
const EstimateCostBlock = ({ 
  mode = "view", 
  data = {},
  onUnitChange,
}) => {
  const estimateData = {
    unit: data.unit || "RMB",
    filterFee: data.filterFee || "0.00",
    basicRewards: data.basicRewards || "0.00",
    addOnRewards: data.addOnRewards || "0.00",
    totalRewards: data.totalRewards || "0.00",
    managementFee: data.managementFee || "0.00",
    totalEstimateFee: data.totalEstimateFee || "0.00",
  }

  const isViewMode = mode === "view"
  const isDisabled = isViewMode // All inputs disabled in view mode

  return (
    <Card style={{ borderRadius: "8px", overflow: "hidden" }}>
      {/* Header - Green */}
      <div
        className="p-3"
        style={{
          backgroundColor: "#E6F8F3",
          color: "#1a5d3a",
          fontWeight: "600",
          fontSize: "1.1rem",
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px",
        }}
      >
        Estimate Cost
      </div>

      <CardBody>
        {/* Row 1: Unit */}
        <Row className="mb-3">
          <Col md={12} className="d-flex align-items-center justify-content-between">
            <Label className="col-form-label mb-0">Unit</Label>
            <div style={{ width: "250px" }}>
              <Input
                type="select"
                className="form-control"
                value={estimateData.unit.toLowerCase()}
                onChange={onUnitChange}
                disabled={isDisabled}
              >
                <option value="free">Free</option>
                <option value="rmb">RMB</option>
                <option value="usd">USD</option>
                <option value="thb">THB</option>
              </Input>
            </div>
          </Col>
        </Row>

        {/* Row 2: Filter Fee */}
        <Row className="mb-3 align-items-center">
          <Col md={12} className="d-flex align-items-center justify-content-between">
            <Label className="col-form-label mb-0">Filter Fee</Label>
            <div className="d-flex align-items-center" style={{ width: "250px" }}>
              <Input
                type="text"
                className="form-control"
                value={estimateData.filterFee}
                disabled
              />
              <span className="ms-2" style={{ whiteSpace: "nowrap" }}>/person</span>
            </div>
          </Col>
        </Row>

        {/* Row 3: Basic Rewards */}
        <Row className="mb-3 align-items-center">
          <Col md={12} className="d-flex align-items-center justify-content-between">
            <Label className="col-form-label mb-0">Basic Rewards</Label>
            <div className="d-flex align-items-center" style={{ width: "250px" }}>
              <Input
                type="text"
                className="form-control"
                value={estimateData.basicRewards}
                disabled
              />
              <span className="ms-2" style={{ whiteSpace: "nowrap" }}>/person</span>
            </div>
          </Col>
        </Row>

        {/* Row 4: Add on Rewards - Hidden for now */}
        {/* <Row className="mb-3 align-items-center">
          <Col md={12} className="d-flex align-items-center justify-content-between">
            <Label className="col-form-label mb-0">Add on Rewards</Label>
            <div className="d-flex align-items-center" style={{ width: "250px" }}>
              <Input
                type="text"
                className="form-control"
                value={estimateData.addOnRewards}
                disabled
              />
              <span className="ms-2" style={{ whiteSpace: "nowrap" }}>/person</span>
            </div>
          </Col>
        </Row> */}

        {/* Row 5: Total Rewards */}
        <Row className="mb-3 align-items-center">
          <Col md={12} className="d-flex align-items-center justify-content-between">
            <Label className="col-form-label mb-0">Total Rewards</Label>
            <div style={{ width: "250px" }}>
              <Input
                type="text"
                className="form-control"
                value={estimateData.totalRewards}
                disabled
              />
            </div>
          </Col>
        </Row>

        {/* Row 6: Management Fee 10% */}
        <Row className="mb-3 align-items-center">
          <Col md={12} className="d-flex align-items-center justify-content-between">
            <Label className="col-form-label mb-0">Management Fee 10%</Label>
            <div style={{ width: "250px" }}>
              <Input
                type="text"
                className="form-control"
                value={estimateData.managementFee}
                disabled
              />
            </div>
          </Col>
        </Row>
      </CardBody>

      {/* Total Estimate Fee - Gray Footer */}
      <div
        className="p-3"
        style={{
          backgroundColor: "#f8f9fa",
          borderTop: "1px solid #e9ecef",
        }}
      >
        <Row className="align-items-center">
          <Col md={12} className="d-flex align-items-center justify-content-between">
            <span style={{ fontWeight: "600", color: "#495057" }}>
              Total Estimate Fee (Include Tax)
            </span>
            <span style={{ color: "#495057", textAlign: "right" }}>
              <span style={{ fontWeight: "700", fontSize: "18px" }}>{estimateData.totalEstimateFee}</span>
              <span style={{ fontWeight: "400", fontSize: "18px" }}> {estimateData.unit}</span>
            </span>
          </Col>
        </Row>
      </div>
    </Card>
  )
}

EstimateCostBlock.propTypes = {
  mode: PropTypes.oneOf(["view", "edit"]),
  data: PropTypes.shape({
    unit: PropTypes.string,
    filterFee: PropTypes.string,
    basicRewards: PropTypes.string,
    addOnRewards: PropTypes.string,
    totalRewards: PropTypes.string,
    managementFee: PropTypes.string,
    totalEstimateFee: PropTypes.string,
  }),
  onUnitChange: PropTypes.func,
}

export default EstimateCostBlock
